/**
 * useLeagueConfig — React hook for reading per-league configuration.
 *
 * Reads from the league_configs table via Supabase. Falls back to
 * the competition config derived from the league's settings JSONB,
 * and ultimately to DEFAULT_COMPETITION defaults.
 *
 * Usage:
 *   const { config, competition, isLoading } = useLeagueConfig();
 *   // config.total_matchdays, config.tdd_limit, etc.
 *   // competition.name, competition.flag, etc.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  DEFAULT_COMPETITION,
  getCompetitionById,
  getCompetitionFromSettings,
  computeDefaultSplits,
  type CompetitionConfig,
  type SeasonSplit,
} from '@/lib/competitions';

export interface ScoringRules {
  win: number;
  draw: number;
  loss: number;
  red_card: number;
  win_by_3_plus_bonus: number;
}

export const DEFAULT_SCORING_RULES: ScoringRules = {
  win: 3,
  draw: 1,
  loss: 0,
  red_card: -2,
  win_by_3_plus_bonus: 1,
};

export interface PrizeEntry {
  place: number;
  label: string;
  amount: number;
}

export interface LastPlacePenalty {
  amount: number;
  paidTo: string;
  description: string;
}

export interface PrizeStructure {
  currency: string;
  prizes: PrizeEntry[];
  lastPlacePenalty: LastPlacePenalty;
}

export interface EmailConfig {
  enabled_emails: {
    matchday_open: boolean;
    matchday_reminder: boolean;
    matchday_selections: boolean;
    matchday_results: boolean;
  };
  reminder_cadence: string[];
}

export const DEFAULT_EMAIL_CONFIG: EmailConfig = {
  enabled_emails: {
    matchday_open: true,
    matchday_reminder: true,
    matchday_selections: true,
    matchday_results: true,
  },
  reminder_cadence: ['3d', '24h', '6h'],
};

export interface LeagueConfig {
  id: string;
  league_id: string;
  api_football_league_id: number;
  api_round_format: string;
  competition_name: string;
  competition_code: string;
  country: string;
  total_matchdays: number;
  season_format: string;
  season_splits: SeasonSplit[] | null;
  team_pick_limit: number;
  tdd_limit: number;
  tdd_ineligible_rule: string;
  tdd_ineligible_count: number;
  deadline_buffer_minutes: number;
  scoring_rules: ScoringRules;
  prize_structure: PrizeStructure | null;
  has_promotion: boolean;
  email_config: EmailConfig;
}

const DEFAULT_LEAGUE_CONFIG: LeagueConfig = {
  id: 'default',
  league_id: 'default',
  api_football_league_id: DEFAULT_COMPETITION.id,
  api_round_format: 'Regular Season - {matchday}',
  competition_name: DEFAULT_COMPETITION.name,
  competition_code: DEFAULT_COMPETITION.code,
  country: DEFAULT_COMPETITION.country,
  total_matchdays: DEFAULT_COMPETITION.matchdays,
  season_format: DEFAULT_COMPETITION.seasonFormat,
  season_splits: DEFAULT_COMPETITION.seasonSplits
    ? DEFAULT_COMPETITION.seasonSplits.map(s => ({
        name: s.name,
        startMatchday: s.startMatchday,
        endMatchday: s.endMatchday,
      }))
    : null,
  team_pick_limit: 2,
  tdd_limit: 4,
  tdd_ineligible_rule: 'top_n',
  tdd_ineligible_count: DEFAULT_COMPETITION.tddIneligibleCount,
  deadline_buffer_minutes: 1,
  scoring_rules: DEFAULT_SCORING_RULES,
  prize_structure: null,
  has_promotion: false,
  email_config: DEFAULT_EMAIL_CONFIG,
};

/**
 * Fetch league config for a specific league ID.
 * Tries league_configs table first, then falls back to league settings, then DEFAULT_COMPETITION defaults.
 */
async function fetchLeagueConfig(leagueId: string): Promise<LeagueConfig> {
  // Try league_configs table first
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const { data, error } = await (supabase
    .from('league_configs' as any)
    .select('*')
    .eq('league_id', leagueId)
    .single() as any);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  if (!error && data) {
    const row = data as Record<string, unknown>;
    // Normalize season_splits from DB snake_case to frontend camelCase
    const rawSplits = row.season_splits as Array<Record<string, unknown>> | null;
    const normalizedSplits: SeasonSplit[] | null = rawSplits
      ? rawSplits.map(s => ({
          name: (s.name as string) || '',
          startMatchday: (s.startMatchday ?? s.start_matchday) as number,
          endMatchday: (s.endMatchday ?? s.end_matchday) as number,
        }))
      : null;
    // Parse scoring_rules from JSONB, falling back to defaults
    const rawScoringRules = row.scoring_rules as Record<string, number> | null;
    const scoringRules: ScoringRules = rawScoringRules
      ? { ...DEFAULT_SCORING_RULES, ...rawScoringRules }
      : DEFAULT_SCORING_RULES;
    // Parse prize_structure from JSONB (nullable)
    const prizeStructure = (row.prize_structure as PrizeStructure | null) ?? null;
    const hasPromotion = (row.has_promotion as boolean) ?? false;
    // Parse email_config from JSONB, falling back to defaults
    const rawEmailConfig = row.email_config as Partial<EmailConfig> | null;
    const emailConfig: EmailConfig = rawEmailConfig
      ? {
          enabled_emails: { ...DEFAULT_EMAIL_CONFIG.enabled_emails, ...rawEmailConfig.enabled_emails },
          reminder_cadence: rawEmailConfig.reminder_cadence ?? DEFAULT_EMAIL_CONFIG.reminder_cadence,
        }
      : DEFAULT_EMAIL_CONFIG;
    return { ...(row as unknown as LeagueConfig), season_splits: normalizedSplits, scoring_rules: scoringRules, prize_structure: prizeStructure, has_promotion: hasPromotion, email_config: emailConfig };
  }

  // Fallback: derive from league's settings.competition_id
  const { data: leagueData } = await supabase
    .from('leagues')
    .select('settings')
    .eq('id', leagueId)
    .single();

  if (leagueData?.settings) {
    const settings = leagueData.settings as Record<string, unknown>;
    if (typeof settings.competition_id === 'number') {
      const comp = getCompetitionById(settings.competition_id);
      return {
        ...DEFAULT_LEAGUE_CONFIG,
        league_id: leagueId,
        api_football_league_id: comp.id,
        competition_name: comp.name,
        competition_code: comp.code,
        country: comp.country,
        total_matchdays: comp.matchdays,
        tdd_ineligible_count: comp.tddIneligibleCount,
        has_promotion: comp.hasPromotion ?? false,
        season_splits: comp.seasonSplits
          ? comp.seasonSplits.map(s => ({
              name: s.name,
              startMatchday: s.startMatchday,
              endMatchday: s.endMatchday,
            }))
          : null,
      };
    }
  }

  // Final fallback: DEFAULT_COMPETITION defaults
  return { ...DEFAULT_LEAGUE_CONFIG, league_id: leagueId };
}

export interface UseLeagueConfigResult {
  /** The full league config object */
  config: LeagueConfig;
  /** The matching competition from the static registry */
  competition: CompetitionConfig;
  /** Competition ID — undefined while config is loading to prevent premature fetches */
  competitionId: number | undefined;
  /** Whether the config is still loading */
  isLoading: boolean;
  /** Error, if any */
  error: Error | null;
  /** Total matchdays for convenience */
  totalMatchdays: number;
  /** Season splits for convenience (always resolved, never null) */
  seasonSplits: SeasonSplit[];
  /** Scoring rules for convenience */
  scoringRules: ScoringRules;
  /** Prize structure for convenience (null if not configured) */
  prizeStructure: PrizeStructure | null;
  /** Whether competition has promotion/relegation */
  hasPromotion: boolean;
  /** Email configuration for the league */
  emailConfig: EmailConfig;
}

/**
 * Hook to get the current league's configuration.
 * Uses the current membership's league_id from AuthContext.
 * Optionally accepts an explicit leagueId override.
 */
export function useLeagueConfig(leagueIdOverride?: string | null): UseLeagueConfigResult {
  const { currentMembership } = useAuth();
  const leagueId = leagueIdOverride ?? currentMembership?.league_id;

  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['league-config', leagueId],
    queryFn: () => fetchLeagueConfig(leagueId!),
    enabled: !!leagueId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const resolvedConfig = config ?? DEFAULT_LEAGUE_CONFIG;
  const configIsLoading = !!leagueId && isLoading;
  const competition = getCompetitionById(resolvedConfig.api_football_league_id);

  // Derive season splits: DB config → competition config → auto-compute from matchdays
  const seasonSplits = resolvedConfig.season_splits
    ?? competition.seasonSplits
    ?? computeDefaultSplits(resolvedConfig.total_matchdays);

  return {
    config: resolvedConfig,
    competition,
    // undefined while loading so hooks don't fire with the DEFAULT_COMPETITION default
    competitionId: configIsLoading ? undefined : resolvedConfig.api_football_league_id,
    isLoading: configIsLoading,
    error: error as Error | null,
    totalMatchdays: resolvedConfig.total_matchdays,
    seasonSplits,
    scoringRules: resolvedConfig.scoring_rules,
    prizeStructure: resolvedConfig.prize_structure,
    hasPromotion: resolvedConfig.has_promotion,
    emailConfig: resolvedConfig.email_config,
  };
}

export { DEFAULT_LEAGUE_CONFIG };
